/* Portal auth — access token in memory, refresh token in an httpOnly cookie.
   On mount we try a silent refresh + /me so a returning customer stays signed
   in across reloads. */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import * as api from '@/portal/api/client';
import type { UserAuthInfo } from '@/portal/api/types';

type Status = 'loading' | 'authed' | 'anon';

interface AuthState {
  status: Status;
  user: UserAuthInfo | null;
  mustChangePassword: boolean;
  login: (username: string, password: string) => Promise<void>;
  acceptInvite: (token: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<UserAuthInfo | null>(null);
  const [mustChange, setMustChange] = useState(false);
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      const ok = await api.refreshSession();
      if (!ok) {
        setStatus('anon');
        return;
      }
      try {
        const me = await api.fetchMe();
        setUser(me);
        setMustChange(me.must_change_password);
        setStatus('authed');
      } catch {
        setStatus('anon');
      }
    })();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.login(username, password);
    setUser(res.user);
    setMustChange(res.user.must_change_password);
    setStatus('authed');
  }, []);

  const acceptInvite = useCallback(async (token: string, password: string) => {
    const res = await api.acceptInvite(token, password);
    setUser(res.user);
    setMustChange(res.user.must_change_password);
    setStatus('authed');
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await api.changePassword(currentPassword, newPassword);
    setMustChange(false);
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    setMustChange(false);
    setStatus('anon');
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, user, mustChangePassword: mustChange, login, acceptInvite, changePassword, logout }),
    [status, user, mustChange, login, acceptInvite, changePassword, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
